import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BobineLogo from '../components/BobineLogo';
import MobileNav from '../components/MobileNav';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

const FINISHES = [
  {
    id: 'livre_linen',
    name: "L'Essentiel",
    finish: 'Lin',
    price: 49,
    maxPhotos: 30,
    tagline: "L'ouvrage idéal pour vos 30 premiers Trésors",
    features: ['Couverture rigide en lin naturel', 'Papier mat premium 200g', 'Marquage doré'],
  },
  {
    id: 'livre_heritage',
    name: "La Chronique",
    finish: 'Cuir',
    price: 79,
    maxPhotos: 80,
    tagline: "Le récit d'une vie en 80 Trésors",
    features: ['Couverture cuir grainé avec dorure', 'Papier photo satiné 250g', 'Coffret inclus'],
    popular: true,
  },
  {
    id: 'livre_silk',
    name: 'Le Grand Récit',
    finish: 'Soie',
    price: 129,
    maxPhotos: 200,
    tagline: "L'épopée familiale complète : 200 Trésors",
    features: ['Soie brodée main', "Tranche dorée à la feuille d'or", 'Écrin luxe'],
  },
];

const serif = "'Georgia', 'Times New Roman', serif";

const LivrePhotoPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('bobine_token');
  const [step, setStep] = useState(1);
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [finish, setFinish] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/photos`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setPhotos(d.photos || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const chosenFinish = FINISHES.find(f => f.id === finish);
  const maxPhotos = chosenFinish?.maxPhotos || 30;

  const togglePhoto = useCallback((id) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= maxPhotos) return prev;
      return [...prev, id];
    });
  }, [maxPhotos]);

  const handleOrder = async () => {
    if (!finish || selected.length === 0) return;
    setSubmitting(true);
    try {
      const orderRes = await fetch(`${API}/book/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo_ids: selected, finish }),
      });
      const orderData = await orderRes.json();
      if (orderData.status !== 'success') throw new Error(orderData.detail || 'Erreur');

      const checkoutRes = await fetch(`${API}/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_id: finish, origin_url: window.location.origin }),
      });
      const checkoutData = await checkoutRes.json();
      if (checkoutData.url) window.location.href = checkoutData.url;
    } catch (e) {
      alert(e.message || 'Erreur lors de la commande');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#000' }}>
      <header style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <BobineLogo size={22} />
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-dashboard"
              style={{ fontFamily: serif, fontSize: 12, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.3)' }}>
              Mon Coffre-fort
            </button>
          </div>
          <MobileNav items={[
            { label: 'Retour', action: () => navigate(-1) },
            { label: 'Accueil', action: () => navigate('/') },
            { label: 'Mon Coffre-fort', action: () => navigate('/dashboard') },
          ]} />
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-5xl">
        {/* Bouton Retour identique à la page Offres */}
        <div style={{ marginBottom: 24 }}>
          <button onClick={() => navigate('/')} data-testid="back-btn"
            style={{ fontFamily: serif, fontSize: 14, padding: '10px 20px', borderRadius: 8, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.3)', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, transition: 'all 0.3s' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.08)'; }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Retour
          </button>
        </div>

        {/* Invitation à se connecter si pas de token */}
        {!token && (
          <div data-testid="livre-login-prompt" style={{
            textAlign: 'center', padding: '40px 24px', marginBottom: 32,
            border: '1px solid rgba(212,175,55,0.2)', borderRadius: 14,
            backgroundColor: 'rgba(212,175,55,0.03)',
          }}>
            <p style={{ fontFamily: serif, fontSize: 16, color: '#D4AF37', marginBottom: 8 }}>
              Connectez-vous pour creer votre Livre d'Art
            </p>
            <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', marginBottom: 20 }}>
              Selectionnez vos plus beaux Tresors et commandez un livre unique
            </p>
            <button onClick={() => navigate('/compte?redirect=/livre-photo')} style={{
              fontFamily: serif, fontSize: 14, padding: '12px 32px', borderRadius: 8,
              background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', cursor: 'pointer', fontWeight: 600,
            }}>
              Se connecter
            </button>
          </div>
        )}

        {/* Stepper */}
        <div className="flex items-center justify-center gap-3 mb-10" data-testid="livre-stepper">
          {['Finition', 'Sélection', 'Commande'].map((label, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: serif, fontSize: 14, fontWeight: 600,
                  backgroundColor: step > i + 1 ? '#D4AF37' : step === i + 1 ? 'rgba(212,175,55,0.15)' : 'transparent',
                  color: step > i + 1 ? '#000' : step === i + 1 ? '#D4AF37' : '#D4AF37',
                  border: `1px solid ${step >= i + 1 ? '#D4AF37' : 'rgba(212,175,55,0.15)'}`,
                  transition: 'all 0.3s',
                }}>
                  {step > i + 1 ? '\u2713' : i + 1}
                </div>
                <span style={{ fontFamily: serif, fontSize: 13, color: step >= i + 1 ? '#D4AF37' : '#D4AF37' }} className="hidden sm:inline">
                  {label}
                </span>
              </div>
              {i < 2 && <div style={{ width: 40, height: 1, backgroundColor: step > i + 1 ? '#D4AF37' : 'rgba(212,175,55,0.15)' }} />}
            </div>
          ))}
        </div>

        {/* STEP 1: Finish Selection */}
        {step === 1 && (
          <div data-testid="step-finish">
            <div className="text-center mb-8">
              <h1 style={{ fontFamily: serif, fontSize: 28, color: '#D4AF37', marginBottom: 8 }}>
                Choisissez votre Livre
              </h1>
              <p style={{ fontFamily: serif, fontSize: 14, color: '#FAF3E0' }}>
                Chaque finition est un écrin unique pour vos souvenirs.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {FINISHES.map(f => (
                <div
                  key={f.id}
                  data-testid={`finish-${f.id}`}
                  onClick={() => { setFinish(f.id); setSelected([]); }}
                  style={{
                    position: 'relative', borderRadius: 14, padding: '28px 20px', cursor: 'pointer',
                    border: finish === f.id ? '2px solid #D4AF37' : f.popular ? '1px solid rgba(212,175,55,0.4)' : '1px solid rgba(212,175,55,0.15)',
                    backgroundColor: finish === f.id ? 'rgba(212,175,55,0.06)' : 'transparent',
                    transition: 'all 0.3s',
                  }}
                >
                  {f.popular && (
                    <div style={{
                      position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                      fontFamily: serif, fontSize: 10, fontWeight: 600, letterSpacing: 1.5,
                      color: '#000', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', padding: '3px 12px', borderRadius: 10, textTransform: 'uppercase',
                    }}>
                      Populaire
                    </div>
                  )}
                  <h3 style={{ fontFamily: serif, fontSize: 20, color: '#D4AF37', textAlign: 'center', marginBottom: 4 }}>
                    {f.name}
                  </h3>
                  <p style={{ fontFamily: serif, fontSize: 11, color: '#D4AF37', textAlign: 'center', fontStyle: 'italic', marginBottom: 12 }}>
                    {f.tagline}
                  </p>
                  <div style={{ textAlign: 'center', marginBottom: 12 }}>
                    <span style={{ fontFamily: serif, fontSize: 36, color: '#D4AF37', fontWeight: 500 }}>{f.price}</span>
                    <span style={{ fontFamily: serif, fontSize: 16, color: '#D4AF37' }}>€</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 12 }}>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontFamily: serif, fontSize: 16, color: '#D4AF37', fontWeight: 500 }}>{f.maxPhotos}</p>
                      <p style={{ fontFamily: serif, fontSize: 9, color: '#FAF3E0', textTransform: 'uppercase', letterSpacing: 1 }}>Photos</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <p style={{ fontFamily: serif, fontSize: 16, color: '#D4AF37', fontWeight: 500 }}>{f.finish}</p>
                      <p style={{ fontFamily: serif, fontSize: 9, color: '#FAF3E0', textTransform: 'uppercase', letterSpacing: 1 }}>Finition</p>
                    </div>
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {f.features.map((feat, i) => (
                      <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 5 }}>
                        <span style={{ color: '#D4AF37', fontSize: 10, marginTop: 2 }}>&#10003;</span>
                        <span style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0', lineHeight: 1.4 }}>{feat}</span>
                      </li>
                    ))}
                  </ul>
                  {finish === f.id && (
                    <div style={{
                      position: 'absolute', top: 12, right: 12, width: 24, height: 24, borderRadius: '50%', background: 'linear-gradient(145deg, #D4AF37, #B8860B)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <button
                onClick={() => { if (finish) setStep(2); }}
                disabled={!finish}
                data-testid="next-to-selection"
                style={{
                  fontFamily: serif, fontSize: 16, fontWeight: 600, padding: '14px 40px',
                  borderRadius: 10, cursor: finish ? 'pointer' : 'not-allowed',
                  backgroundColor: finish ? '#D4AF37' : 'rgba(212,175,55,0.2)',
                  color: finish ? '#000' : '#D4AF37',
                  border: 'none', transition: 'all 0.3s',
                  boxShadow: finish ? '0 4px 20px rgba(212,175,55,0.3)' : 'none',
                }}
              >
                Sélectionner mes Trésors
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Photo Selection */}
        {step === 2 && chosenFinish && (
          <div data-testid="step-selection">
            <div className="text-center mb-8">
              <h1 style={{ fontFamily: serif, fontSize: 28, color: '#D4AF37', marginBottom: 8 }}>
                Sélectionnez vos Trésors
              </h1>
              <p style={{ fontFamily: serif, fontSize: 14, color: '#FAF3E0' }}>
                {chosenFinish.name} — jusqu'à {maxPhotos} photos en finition {chosenFinish.finish}.
              </p>
              <div data-testid="selection-count" style={{
                display: 'inline-block', marginTop: 12, padding: '6px 16px', borderRadius: 20,
                fontFamily: serif, fontSize: 13,
                border: '1px solid rgba(212,175,55,0.3)',
                color: selected.length >= maxPhotos ? '#000' : '#D4AF37',
                backgroundColor: selected.length >= maxPhotos ? '#D4AF37' : 'transparent',
                transition: 'all 0.3s',
              }}>
                {selected.length} / {maxPhotos} sélectionnées
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full" style={{ width: 48, height: 48, border: '2px solid transparent', borderTopColor: '#D4AF37' }} />
              </div>
            ) : photos.length === 0 ? (
              <div className="text-center py-16">
                <p style={{ fontFamily: serif, fontSize: 16, color: '#FAF3E0', marginBottom: 20 }}>
                  Votre Coffre-fort est vide. Déposez d'abord vos souvenirs.
                </p>
                <button onClick={() => navigate('/')} data-testid="go-upload"
                  style={{ fontFamily: serif, fontSize: 14, padding: '12px 28px', borderRadius: 8, cursor: 'pointer', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none' }}>
                  Déposer des souvenirs
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3" data-testid="photo-grid">
                  {photos.map(photo => {
                    const isSelected = selected.includes(photo.id);
                    const idx = isSelected ? selected.indexOf(photo.id) + 1 : null;
                    return (
                      <div
                        key={photo.id}
                        data-testid={`photo-select-${photo.id}`}
                        onClick={() => togglePhoto(photo.id)}
                        style={{
                          position: 'relative', borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                          border: isSelected ? '2px solid #D4AF37' : '1px solid rgba(212,175,55,0.1)',
                          transition: 'all 0.2s', aspectRatio: '1',
                          opacity: !isSelected && selected.length >= maxPhotos ? 0.4 : 1,
                        }}
                      >
                        <img src={`${API}/photos/${photo.id}/thumbnail?token=${token}`} alt={photo.filename} loading="lazy"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        {isSelected && (
                          <div style={{
                            position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%',
                            background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontFamily: serif, fontSize: 11, fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                          }}>
                            {idx}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-center gap-4 mt-8">
                  <button onClick={() => setStep(1)} data-testid="back-to-finish"
                    style={{ fontFamily: serif, fontSize: 14, padding: '12px 28px', borderRadius: 8, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.3)' }}>
                    Retour
                  </button>
                  <button
                    onClick={() => { if (selected.length > 0) setStep(3); }}
                    disabled={selected.length === 0}
                    data-testid="next-to-recap"
                    style={{
                      fontFamily: serif, fontSize: 16, fontWeight: 600, padding: '14px 40px',
                      borderRadius: 10, cursor: selected.length > 0 ? 'pointer' : 'not-allowed',
                      backgroundColor: selected.length > 0 ? '#D4AF37' : 'rgba(212,175,55,0.2)',
                      color: selected.length > 0 ? '#000' : '#D4AF37',
                      border: 'none', transition: 'all 0.3s',
                    }}
                  >
                    Voir le récapitulatif
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* STEP 3: Recap & Checkout */}
        {step === 3 && chosenFinish && (
          <div data-testid="step-recap">
            <div className="text-center mb-8">
              <h1 style={{ fontFamily: serif, fontSize: 28, color: '#D4AF37', marginBottom: 8 }}>Votre commande</h1>
            </div>

            <div className="max-w-lg mx-auto" style={{
              border: '1px solid rgba(212,175,55,0.25)', borderRadius: 14,
              padding: '32px 28px', backgroundColor: 'rgba(212,175,55,0.02)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
                <div>
                  <h3 style={{ fontFamily: serif, fontSize: 20, color: '#D4AF37', marginBottom: 4 }}>{chosenFinish.name}</h3>
                  <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0' }}>Finition {chosenFinish.finish}</p>
                </div>
                <p style={{ fontFamily: serif, fontSize: 32, color: '#D4AF37', fontWeight: 500 }}>
                  {chosenFinish.price}<span style={{ fontSize: 16, color: '#D4AF37' }}>€</span>
                </p>
              </div>

              <div style={{ marginBottom: 20 }}>
                <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', marginBottom: 8 }}>
                  {selected.length} Trésor{selected.length > 1 ? 's' : ''} sélectionné{selected.length > 1 ? 's' : ''}
                </p>
                <div className="flex flex-wrap gap-1">
                  {selected.slice(0, 10).map(id => (
                    <img key={id} src={`${API}/photos/${id}/thumbnail?token=${token}`} alt=""
                      style={{ width: 44, height: 44, borderRadius: 4, objectFit: 'cover', border: '1px solid rgba(212,175,55,0.2)' }} />
                  ))}
                  {selected.length > 10 && (
                    <div style={{
                      width: 44, height: 44, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)',
                      fontFamily: serif, fontSize: 11, color: '#D4AF37',
                    }}>
                      +{selected.length - 10}
                    </div>
                  )}
                </div>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', borderTop: '1px solid rgba(212,175,55,0.1)', paddingTop: 16 }}>
                {chosenFinish.features.map((f, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ color: '#D4AF37', fontSize: 11 }}>&#10003;</span>
                    <span style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0' }}>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center justify-center gap-4">
                <button onClick={() => setStep(2)} data-testid="back-to-selection"
                  style={{ fontFamily: serif, fontSize: 14, padding: '12px 28px', borderRadius: 8, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.3)' }}>
                  Modifier
                </button>
                <button onClick={handleOrder} disabled={submitting} data-testid="checkout-livre"
                  style={{
                    fontFamily: serif, fontSize: 16, fontWeight: 600, padding: '14px 40px',
                    borderRadius: 10, cursor: submitting ? 'wait' : 'pointer',
                    background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none',
                    boxShadow: '0 4px 20px rgba(212,175,55,0.3)',
                    opacity: submitting ? 0.6 : 1, transition: 'all 0.3s',
                  }}
                >
                  {submitting ? 'Redirection...' : `Commander — ${chosenFinish.price}€`}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LivrePhotoPage;
